import { useEffect, useRef, useState } from 'react';

/**
 * One tooltip for the whole app, mounted once. It names what is under the pointer or keyboard
 * focus without every button having to wire its own:
 *
 * - an element with `data-tip` shows that text;
 * - a `title` is taken over (the browser's own tooltip is slow and unstyled) and kept as the
 *   accessible name when the element has no other;
 * - an icon-only button or link shows its `aria-label`.
 *
 * Touch input gets no tooltip: there is no hover, and the label is read out by screen readers.
 */
const SHOW_DELAY_MS = 350;
const GAP = 6;
const TARGET = 'button, a, [role="button"], [role="switch"], [role="tab"], [data-tip], [title]';

type Tip = { text: string; x: number; y: number; above: boolean };

function hasVisibleText(el: Element): boolean {
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE && node.textContent?.trim()) return true;
    if (node instanceof HTMLElement && !node.matches('svg, [aria-hidden="true"], .sr-only')) {
      if (node.textContent?.trim() && getComputedStyle(node).display !== 'none') return true;
    }
  }
  return false;
}

/** The text to show for an element, or null; moves a `title` into `data-tip` on the way. */
export function tipText(el: HTMLElement): string | null {
  const title = el.getAttribute('title');
  if (title) {
    el.removeAttribute('title');
    if (!el.dataset.tip) el.dataset.tip = title;
    if (!el.getAttribute('aria-label') && !hasVisibleText(el)) el.setAttribute('aria-label', title);
  }
  if (el.dataset.tip) return el.dataset.tip;
  const label = el.getAttribute('aria-label');
  if (label && el.matches('button, a, [role="button"]') && !hasVisibleText(el)) return label;
  return null;
}

function findTarget(start: EventTarget | null): HTMLElement | null {
  let el = start instanceof Element ? start.closest<HTMLElement>(TARGET) : null;
  while (el) {
    if (tipText(el)) return el;
    el = el.parentElement?.closest<HTMLElement>(TARGET) ?? null;
  }
  return null;
}

function place(el: HTMLElement, text: string): Tip {
  const r = el.getBoundingClientRect();
  const above = r.bottom + 40 > window.innerHeight;
  return {
    text,
    x: Math.min(Math.max(r.left + r.width / 2, 12), window.innerWidth - 12),
    y: above ? r.top - GAP : r.bottom + GAP,
    above,
  };
}

export function TooltipLayer() {
  const [tip, setTip] = useState<Tip | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const current = useRef<HTMLElement | null>(null);
  const bubble = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const hide = () => {
      window.clearTimeout(timer.current);
      current.current = null;
      setTip(null);
    };
    const show = (el: HTMLElement, delay: number) => {
      window.clearTimeout(timer.current);
      current.current = el;
      timer.current = window.setTimeout(() => {
        const text = current.current === el ? tipText(el) : null;
        if (text && el.isConnected) setTip(place(el, text));
      }, delay);
    };
    const onOver = (event: PointerEvent) => {
      if (event.pointerType === 'touch') return;
      const el = findTarget(event.target);
      if (el === current.current) return;
      if (!el) return hide();
      show(el, SHOW_DELAY_MS);
    };
    const onFocus = (event: FocusEvent) => {
      const el = findTarget(event.target);
      if (!el || !(event.target as HTMLElement).matches(':focus-visible')) return;
      show(el, 0);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hide();
    };
    document.addEventListener('pointerover', onOver);
    document.addEventListener('focusin', onFocus);
    document.addEventListener('focusout', hide);
    document.addEventListener('pointerdown', hide);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('blur', hide);
    return () => {
      hide();
      document.removeEventListener('pointerover', onOver);
      document.removeEventListener('focusin', onFocus);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('pointerdown', hide);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('blur', hide);
    };
  }, []);

  // Keep the bubble inside the window once its width is known.
  useEffect(() => {
    const node = bubble.current;
    if (!node || !tip) return;
    const half = node.offsetWidth / 2;
    const x = Math.min(Math.max(tip.x, half + 8), window.innerWidth - half - 8);
    node.style.left = `${x}px`;
  }, [tip]);

  if (!tip) return null;
  return (
    <div
      ref={bubble}
      role="tooltip"
      className="nv-tooltip"
      data-above={tip.above}
      style={{ left: tip.x, top: tip.y }}
    >
      {tip.text}
    </div>
  );
}
